import { useState, useEffect, useCallback, useRef } from 'react';
import { ThumbsUp, ThumbsDown, Download, Loader2, Filter, MessageSquare } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import axios from 'axios';
import { API_BASE_URL } from '../config/api.config';
import { useAuthStore } from '../store/authStore';
import Swal from 'sweetalert2';

/**
 * Chatbot Feedback Reports (NCHAT-RQ-18)
 * Internal FK team view: aggregated feedback by period, category and course + CSV export
 */

const CATEGORY_LABELS: Record<string, string> = {
  incorrect_answer: 'Incorrect answer',
  incomplete_answer: 'Incomplete answer',
  did_not_answer: 'Did not answer the question',
  other: 'Other',
};

interface FeedbackItem {
  id: string;
  message_id: string;
  session_id: string;
  user: string;
  question: string;
  answer: string;
  rating: 'positive' | 'negative';
  category: string | null;
  comment: string | null;
  courses: string[];
  oeCodes: string[];
  sources: string[];
  created_at: string;
}

interface FeedbackSummary {
  total: number;
  positive: number;
  negative: number;
  byCategory: Record<string, number>;
  byCourse: Record<string, number>;
}

interface Filters {
  from: string;
  to: string;
  rating: string;
  category: string;
  course: string;
}

const EMPTY_FILTERS: Filters = { from: '', to: '', rating: '', category: '', course: '' };
const PAGE_SIZE = 20;

export function ChatbotFeedback() {
  const { accessToken } = useAuthStore();
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [summary, setSummary] = useState<FeedbackSummary | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Course options grow from the data itself (all courses ever seen in results)
  const courseOptionsRef = useRef<Set<string>>(new Set());

  const buildParams = (targetPage?: number) => {
    const params: Record<string, string | number> = {};
    if (filters.from) params.from = filters.from;
    // Include the whole "to" day (backend compares with $lte)
    if (filters.to) params.to = `${filters.to}T23:59:59.999Z`;
    if (filters.rating) params.rating = filters.rating;
    if (filters.category) params.category = filters.category;
    if (filters.course) params.course = filters.course;
    if (targetPage) {
      params.page = targetPage;
      params.limit = PAGE_SIZE;
    }
    return params;
  };

  const loadFeedback = useCallback(async (targetPage: number) => {
    if (!accessToken) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/chatbot/feedback`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: buildParams(targetPage),
      });
      const data = response.data.data;
      setItems(data.items || []);
      setSummary(data.summary || null);
      setTotal(data.total || 0);
      setPage(targetPage);
      Object.keys(data.summary?.byCourse || {}).forEach(c => courseOptionsRef.current.add(c));
    } catch (err: any) {
      console.error('[ChatbotFeedback] Error loading feedback:', err);
      setError(
        err.response?.status === 403
          ? 'You do not have access to feedback reports.'
          : err.response?.data?.error || 'Failed to load feedback data.'
      );
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, filters]);

  useEffect(() => {
    loadFeedback(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const handleApplyFilters = () => loadFeedback(1);

  const handleClearFilters = () => {
    setFilters(EMPTY_FILTERS);
  };

  const handleExport = async () => {
    if (!accessToken) return;
    setIsExporting(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/chatbot/feedback/export`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: buildParams(),
        responseType: 'blob',
      });

      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `chatbot-feedback-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('[ChatbotFeedback] Export failed:', err);
      Swal.fire({
        title: 'Export Failed',
        text: err.response?.data?.error || 'Could not download the CSV file.',
        icon: 'error',
        confirmButtonColor: '#dc2626',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const totalPages = Math.max(Math.ceil(total / PAGE_SIZE), 1);
  const positivePct = summary && summary.total > 0 ? Math.round((summary.positive / summary.total) * 100) : 0;
  const negativePct = summary && summary.total > 0 ? Math.round((summary.negative / summary.total) * 100) : 0;
  const courseOptions = Array.from(courseOptionsRef.current).sort();

  return (
    <div className="notranslate" translate="no">
      <div className="flex items-center justify-between mb-6">
        <PageHeader
          title="Chatbot Feedback"
          description="User feedback on chatbot answers — filter by period, category and course"
          breadcrumbs={[{ label: 'Chatbot Feedback' }]}
        />
        <button
          onClick={handleExport}
          disabled={isExporting || isLoading}
          className="px-6 py-3 bg-gradient-to-r from-[#1e73be] to-[#4A90C8] hover:from-[#1e73be]/90 hover:to-[#4A90C8]/90 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isExporting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-semibold text-gray-700">Filters</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">From</label>
            <input
              type="date"
              value={filters.from}
              onChange={(e) => setFilters(f => ({ ...f, from: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#1e73be] outline-none"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">To</label>
            <input
              type="date"
              value={filters.to}
              onChange={(e) => setFilters(f => ({ ...f, to: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#1e73be] outline-none"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Rating</label>
            <select
              value={filters.rating}
              onChange={(e) => setFilters(f => ({ ...f, rating: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#1e73be] outline-none bg-white"
            >
              <option value="">All</option>
              <option value="positive">👍 Positive</option>
              <option value="negative">👎 Negative</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Category</label>
            <select
              value={filters.category}
              onChange={(e) => setFilters(f => ({ ...f, category: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#1e73be] outline-none bg-white"
            >
              <option value="">All</option>
              {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Course</label>
            <select
              value={filters.course}
              onChange={(e) => setFilters(f => ({ ...f, course: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#1e73be] outline-none bg-white"
            >
              <option value="">All</option>
              {courseOptions.map(course => (
                <option key={course} value={course}>{course}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={handleApplyFilters}
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-[#1e73be] hover:bg-[#1e73be]/90 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
            >
              Apply
            </button>
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg text-sm font-semibold transition-colors"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4">
            <p className="text-xs text-gray-500 mb-1">Total Feedback</p>
            <p className="text-2xl font-bold text-gray-800">{summary.total}</p>
          </div>
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4">
            <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
              <ThumbsUp className="w-3.5 h-3.5 text-green-500" /> Positive
            </p>
            <p className="text-2xl font-bold text-green-600">
              {summary.positive} <span className="text-sm font-medium text-gray-400">({positivePct}%)</span>
            </p>
          </div>
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4">
            <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
              <ThumbsDown className="w-3.5 h-3.5 text-red-500" /> Negative
            </p>
            <p className="text-2xl font-bold text-red-600">
              {summary.negative} <span className="text-sm font-medium text-gray-400">({negativePct}%)</span>
            </p>
          </div>
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4">
            <p className="text-xs text-gray-500 mb-2">By Category</p>
            <div className="flex flex-wrap gap-1.5">
              {Object.keys(summary.byCategory).length === 0 && (
                <span className="text-xs text-gray-400">No categorized feedback</span>
              )}
              {Object.entries(summary.byCategory).map(([cat, count]) => (
                <span
                  key={cat}
                  className="inline-flex items-center px-2 py-0.5 rounded-md bg-orange-50 border border-orange-200 text-xs text-orange-700"
                >
                  {CATEGORY_LABELS[cat] || cat}: {count}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
        {error ? (
          <div className="p-10 text-center text-sm text-red-500">{error}</div>
        ) : isLoading ? (
          <div className="p-10 flex items-center justify-center gap-2 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">Loading feedback...</span>
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No feedback found for the selected filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Rating</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Question</th>
                  <th className="px-4 py-3">Answer</th>
                  <th className="px-4 py-3">Comment</th>
                  <th className="px-4 py-3">Courses</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50 align-top">
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                      {new Date(item.created_at).toLocaleDateString()}
                      <div className="text-xs text-gray-400">
                        {new Date(item.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {item.rating === 'positive' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-green-50 border border-green-200 text-xs text-green-700">
                          <ThumbsUp className="w-3 h-3" /> Positive
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700">
                          <ThumbsDown className="w-3 h-3" /> Negative
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {item.category ? (CATEGORY_LABELS[item.category] || item.category) : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-800 max-w-[220px]">
                      <p className="line-clamp-3" title={item.question}>{item.question}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-600 max-w-[300px]">
                      <p className="line-clamp-3" title={item.answer}>{item.answer}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-600 max-w-[200px]">
                      {item.comment ? <p className="line-clamp-3" title={item.comment}>{item.comment}</p> : '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1">
                        {item.courses.length === 0 && <span className="text-gray-400">—</span>}
                        {item.courses.map(course => (
                          <span
                            key={course}
                            className="inline-flex px-2 py-0.5 rounded-md bg-blue-50 border border-blue-200 text-xs text-blue-700"
                          >
                            {course}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!error && !isLoading && total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-500">
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => loadFeedback(page - 1)}
                disabled={page <= 1 || isLoading}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
              <button
                onClick={() => loadFeedback(page + 1)}
                disabled={page >= totalPages || isLoading}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ChatbotFeedback;
